import React from "react";

import _ from "lodash";

import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";
import useForm from "commons/hooks/useForm";

import { PathParamsWithId, ResponseWithId } from "commons/types/api/common";
import { FormHookType } from "commons/types/form";
import { OptionsType, ResponseType } from "commons/types/http";
import { ObjectPropertyType } from "commons/types/validation";

import { AttachmentType } from "components/ui/AttachmentPicker";

import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { ImagesLanguagesDetails } from "types/api/product";
import { ProductMeasureBody } from "types/api/productMeasure";
import { DesktopOrMobile } from "types/enums";

import {
    calculateStatus,
    converterAttachmentToFileWithPayload,
    createAttachmentType,
} from "utils/utils";
import { getExtraErrorsDescription, notEmptyMedia } from "utils/validators";

export type LanguageOrderableImagesHookParams<T extends ProductMeasureBody> = {
    createImage: (
        body: T,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<ResponseWithId>;
    updateImage: (
        pathParams: PathParamsWithId,
        body: T,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<null>;
    deleteImage: (
        pathParams: PathParamsWithId,
        options?: OptionsType,
        headers?: {
            [x: string]: string;
        }
    ) => ResponseType<null>;
    fieldId: string;
};

type ImagesLanguagesBodyForm = {
    id?: string;
    product_id: string;
    graphical_order: number;
    visible: boolean;
    toDelete: boolean;
    deleted: boolean;
    media: {
        [x in LanguageEnum]: { media: DesktopMobileForm };
    };
};

type BodyWithImages = {
    images: Array<ImagesLanguagesBodyForm>;
};

export type LanguageOrderableImagesHook = {
    initializeOrderableImage: (images: Array<ImagesLanguagesDetails>) => void;
    saveLanguageOrderableImages: (itemId: string) => Promise<unknown>;
    setImage: (
        file: AttachmentType,
        language: LanguageEnum,
        target: DesktopOrMobile,
        index: number
    ) => void;
    deleteRow: (index: number) => void;
    addImage: () => void;
    dragAndDropHook: DragAndDropListHook<HTMLDivElement>;
    formHook: FormHookType<BodyWithImages>;
    visibleItemsLenght: number;
};

function useLanguageOrderableImages<T extends ProductMeasureBody>({
    createImage,
    updateImage,
    deleteImage,
    fieldId,
}: LanguageOrderableImagesHookParams<T>): LanguageOrderableImagesHook {
    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                images: {
                    type: "objectArray",
                    of: {
                        type: "object",
                        validators: [],
                        properties: {
                            graphical_order: {
                                type: "simple",
                                validators: [],
                            },
                            media: {
                                type: "collection",
                                validators: [],
                                of: {
                                    type: "object",
                                    properties: {
                                        media: notEmptyMedia,
                                    },
                                },
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: BodyWithImages = React.useMemo(() => {
        return {
            images: [],
        };
    }, []);

    const formHook = useForm<BodyWithImages>(schema, initialValues, getExtraErrorsDescription);

    const { setForm, form } = formHook;

    const setImage = React.useCallback(
        (file: AttachmentType, language: LanguageEnum, target: DesktopOrMobile, index: number) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                if (
                    newForm.images[index] &&
                    newForm.images[index].media &&
                    newForm.images[index].media[language].media[target]
                ) {
                    if (file) {
                        newForm.images[index].media[language].media[target] = createAttachmentType({
                            filename: file.filename,
                            content: file.content,
                            contentType: file.contentType ?? "",
                            label: file.label,
                            size: file.size,
                            status: calculateStatus(file.filename, file.content),
                        });
                    } else {
                        newForm.images[index].media[language].media[target] = createAttachmentType({
                            label: "",
                        });
                    }
                }

                return newForm;
            });
        },
        [setForm]
    );

    const addImage = React.useCallback(() => {
        setForm((prevForm) => {
            const newForm = _.cloneDeep(prevForm);

            newForm.images.push({
                product_id: "",
                graphical_order: (newForm.images.length + 1) * 10,
                media: {
                    it: {
                        media: {
                            id: undefined,
                            desktop: createAttachmentType({
                                label: "Desktop",
                            }),
                            mobile: createAttachmentType({
                                label: "Mobile",
                            }),
                        },
                    },
                    en: {
                        media: {
                            id: undefined,
                            desktop: createAttachmentType({
                                label: "Desktop",
                            }),
                            mobile: createAttachmentType({
                                label: "Mobile",
                            }),
                        },
                    },
                },
                visible: true,
                toDelete: false,
                deleted: false,
            });

            return newForm;
        });
    }, [setForm]);

    const deleteRow = React.useCallback(
        (index) => {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const finalForm: BodyWithImages = {
                    images: [],
                };

                // if the file is local I just remove the object, if there is an id so it's on the server I set it to delete
                // this to prevent issues with the validity of the form
                newForm.images.forEach((image, imageIndex) => {
                    if (imageIndex === index) {
                        if (image.id) {
                            finalForm.images.push({
                                ...newForm.images[index],
                                visible: false,
                                toDelete: true,
                            });
                        }
                    } else {
                        finalForm.images.push(image);
                    }
                });

                return finalForm;
            });
        },
        [setForm]
    );

    React.useEffect(() => {
        if (form.images.length === 0) {
            setForm(initialValues);
        }
    }, [setForm, initialValues, form.images.length]);

    const onUpdateList = React.useCallback(
        (newList) => {
            setForm((prevList) => {
                const tmpList = _.cloneDeep(prevList);

                tmpList.images = newList;

                tmpList.images.forEach((singleRow, index) => {
                    tmpList.images[index].graphical_order = (index + 1) * 10;
                });

                return tmpList;
            });
        },
        [setForm]
    );

    const dragAndDropHook = useDragAndDropList({ list: form.images, onUpdateList });

    const saveLanguageOrderableImages = React.useCallback(
        (itemId: string) => {
            const promises: Promise<unknown>[] = [];

            form.images.forEach((image, index) => {
                if (image.toDelete) {
                    if (image.id) {
                        const [apiCall] = deleteImage({ id: image.id });

                        apiCall.then(() => {
                            setForm((prevForm) => {
                                const newForm: BodyWithImages = _.cloneDeep(prevForm);

                                newForm.images.forEach((imageToDelete, indexToDelete) => {
                                    if (imageToDelete.id === image.id) {
                                        newForm.images[indexToDelete].toDelete = false;
                                        newForm.images[indexToDelete].deleted = true;
                                        newForm.images[indexToDelete].visible = false;
                                    }
                                });

                                return newForm;
                            });
                        });

                        promises.push(apiCall);
                    }
                } else if (!image.deleted) {
                    const body: ProductMeasureBody = {
                        id: image.id,
                        product_id: "",
                        graphical_order: image.graphical_order,
                        media: {
                            it: {
                                media: {
                                    id: image.media.it.media.id,
                                    desktop: converterAttachmentToFileWithPayload(
                                        image.media.it.media.desktop
                                    ),
                                    mobile: converterAttachmentToFileWithPayload(
                                        image.media.it.media.mobile
                                    ),
                                },
                            },
                            en: {
                                media: {
                                    id: image.media.en.media.id,
                                    desktop: converterAttachmentToFileWithPayload(
                                        image.media.en.media.desktop
                                    ),
                                    mobile: converterAttachmentToFileWithPayload(
                                        image.media.en.media.mobile
                                    ),
                                },
                            },
                        },
                    };

                    if (image.id) {
                        const [apiCall] = updateImage({ id: image.id }, {
                            ...body,
                            [fieldId]: itemId,
                        } as T);

                        promises.push(apiCall);
                    } else {
                        const createBody = {
                            ...body,
                            [fieldId]: itemId,
                        } as T;

                        if (
                            createBody.media.it.media.desktop &&
                            createBody.media.it.media.mobile &&
                            createBody.media.en.media.desktop &&
                            createBody.media.en.media.mobile
                        ) {
                            const [apiCall] = createImage(createBody);

                            apiCall.then((response) => {
                                setForm((prevForm) => {
                                    const newForm: BodyWithImages = _.cloneDeep(prevForm);

                                    newForm.images[index].id = response.id;

                                    return newForm;
                                });
                            });

                            promises.push(apiCall);
                        }
                    }
                }
            });

            return Promise.all(promises);
        },
        [setForm, createImage, updateImage, deleteImage, fieldId, form]
    );

    const visibleItemsLenght = React.useMemo(() => {
        return form.images.reduce((counter, image) => counter + (image.visible ? 1 : 0), 0);
    }, [form]);

    const initializeOrderableImage = React.useCallback(
        (images: Array<ImagesLanguagesDetails>) => {
            const newForm: BodyWithImages = {
                images: [],
            };

            images.forEach((image) => {
                newForm.images.push({
                    id: image.id,
                    product_id: "",
                    graphical_order: image.graphical_order,
                    visible: true,
                    toDelete: false,
                    deleted: false,
                    media: {
                        it: {
                            media: {
                                id: image.media.it.media.id,
                                desktop: createAttachmentType({
                                    id: image.media.it.media.desktop_id,
                                    label: "Desktop",
                                    filename: image.media.it.media.desktop.filename,
                                    status: 2,
                                }),
                                mobile: createAttachmentType({
                                    id: image.media.it.media.mobile_id,
                                    label: "Mobile",
                                    filename: image.media.it.media.mobile.filename,
                                    status: 2,
                                }),
                            },
                        },
                        en: {
                            media: {
                                id: image.media.en.media.id,
                                desktop: createAttachmentType({
                                    id: image.media.en.media.desktop_id,
                                    label: "Desktop",
                                    filename: image.media.en.media.desktop.filename,
                                    status: 2,
                                }),
                                mobile: createAttachmentType({
                                    id: image.media.en.media.mobile_id,
                                    label: "Mobile",
                                    filename: image.media.en.media.mobile.filename,
                                    status: 2,
                                }),
                            },
                        },
                    },
                });
            });

            setForm(newForm);
        },
        [setForm]
    );

    return React.useMemo(
        (): LanguageOrderableImagesHook => ({
            initializeOrderableImage,
            saveLanguageOrderableImages,
            deleteRow,
            setImage,
            addImage,
            dragAndDropHook,
            formHook,
            visibleItemsLenght,
        }),
        [
            initializeOrderableImage,
            saveLanguageOrderableImages,
            setImage,
            addImage,
            deleteRow,
            dragAndDropHook,
            formHook,
            visibleItemsLenght,
        ]
    );
}

export default useLanguageOrderableImages;
